import { FormBlockInstance, LocalizedText } from '../api-types';

export const RATING_TYPE = 'rating';
export const RATING_TYPE_VERSION = 1;

/** How the scale is drawn. Bounded by the server's enum; anything else falls back to labels. */
export type RatingStyle = 'LABELS' | 'NUMERIC' | 'STARS';

export interface RatingLevel {
  key: string;
  label: LocalizedText | null;
  description: LocalizedText | null;
}

export interface RatingConfig {
  levels: RatingLevel[];
  style: RatingStyle;
}

/**
 * The answer shape pro-core's `rating@1` validates.
 *
 * No ordinal. The position is derived from the frozen scale server-side, and an answer carrying one
 * is refused — so sending it would not merely be redundant, it would fail the submit.
 */
export interface RatingAnswer {
  levelKey: string;
}

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asLocalizedText = (value: unknown): LocalizedText | null => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const entries = Object.entries(value as Record<string, unknown>).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string'
  );
  return entries.length ? Object.fromEntries(entries) : null;
};

const asStyle = (value: unknown): RatingStyle =>
  value === 'NUMERIC' || value === 'STARS' ? value : 'LABELS';

/**
 * Read a stored config into the shape this renderer needs.
 *
 * The level ORDER is preserved exactly as stored, and that is load-bearing rather than incidental:
 * the position of a level in this array is its meaning, and it is what the server derives the
 * ordinal from. Sorting these for display would silently disagree with the number recorded against
 * the answer.
 */
export const readRatingConfig = (block: FormBlockInstance): RatingConfig => {
  const config = asRecord(block.config);
  const data = asRecord(config.data);
  const presentation = asRecord(config.presentation);
  const rawLevels = Array.isArray(data.levels) ? data.levels : [];

  return {
    levels: rawLevels
      .map(asRecord)
      .filter(level => typeof level.key === 'string' && level.key.length > 0)
      .map(level => ({
        key: level.key as string,
        label: asLocalizedText(level.label),
        description: asLocalizedText(level.description)
      })),
    style: asStyle(presentation.style)
  };
};

export const readRatingAnswer = (answer: unknown): RatingAnswer | null => {
  const record = asRecord(answer);
  return typeof record.levelKey === 'string' && record.levelKey.length > 0
    ? { levelKey: record.levelKey }
    : null;
};

export const isRatingAnswered = (answer: unknown): boolean =>
  readRatingAnswer(answer) !== null;
